import React, {useCallback, useEffect, Suspense, useState} from 'react';
import {pendoService} from '../services/pendoService.web';
import {showErrorToast} from '../store/errorSlice';
import {NavigationContainer} from '@react-navigation/native';
import {createStackNavigator} from '@react-navigation/stack';
import {useDispatch} from 'react-redux';
import {setNovaAIEnabled} from '../../shared/store/novaAISlice';
import {navigationRef} from '../navigation/navigationRef';
import {setSessionExpiredCallback} from '../../features/auth/api/axiosInstance';
import {timeZoneStorage} from '../../features/auth/api/timeZoneStorage';
import {TIMEZONE_STORAGE_KEY} from '../store/userSlice';
import {
  refreshAccessToken,
  signoutWithBrowser,
  userManager,
} from '../../features/auth/services/authProvider';
import {validateAgencyName} from '../../features/auth/services/validateAgencyNameService';
import {agencyStorage} from '../../features/auth/api/agencyStorage';
import {tabManager} from '../services/tabManager.web';
import {storageService} from '../services/storageService';
import secureStorage from '../services/secureStorage';
import {setLanguage, LANGUAGE_STORAGE_KEY} from '../store/languageSlice';
import {
  setShiftStarted,
  SHIFT_STARTED_KEY,
} from '../store/shiftManagementSlice';
import {
  setSelectedShop,
  SHOP_STORAGE_KEY,
  SelectedShopState,
} from '../store/shopSlice';
import {
  setSelectedTechnicianData,
  setCreatedUserId,
  TECHNICIAN_STORAGE_KEY,
  CREATED_USER_ID_KEY,
  SelectedTechnician,
  DEFAULT_STORE_ROOM_KEY,
  setDefaultStoreRoom,
} from '../store/technicianSlice';
import {AUTH_USER_ID_KEY} from '../../features/auth/api/userIdStorage';
import {setActiveTab, MENU_ACTIVE_TAB_KEY} from '../store/menuSlice';
import {setSelectedRepairId, REPAIR_ID_KEY, setShowRepairHistory, SHOW_REPAIR_HISTORY_KEY} from '../store/repairSlice';
import {Header} from '../components/layout';
import {ActivityIndicator, View} from 'react-native';
import {useScrollbarTheme} from '../hooks/useScrollbarTheme';

// §2.1 — Route-level code splitting: each screen is loaded on demand.
// Named exports from barrel files use the .then(m => ({ default: m.X })) pattern.
const AgencySelectionScreen = React.lazy(() =>
  import('../../features/auth').then(m => ({default: m.AgencySelectionScreen})),
);
const MainLayoutScreen = React.lazy(
  () =>
    import('../../features/layout/screens/MainLayoutScreen/MainLayoutScreen'),
);
const SignatureScreen = React.lazy(
  () => import('../../features/signature/screens/SignatureScreen'),
);
const RepairDetailsScreen = React.lazy(
  () =>
    import(
      '../../features/repairDetails/screens/RepairDetailsScreen/RepairDetailsScreen'
    ),
);
const WorkOrdersScreen = React.lazy(
  () =>
    import(
      '../../features/dashboard/screens/WorkOrdersScreen/WorkOrdersScreen'
    ),
);
const PartsScreen = React.lazy(
  () => import('../../features/parts/screens/PartsScreen'),
);
const ShopHomePage = React.lazy(
  () => import('../../features/shop/screens/ShopHomePage'),
);
const HelpScreen = React.lazy(
  () => import('@features/help/screens/HelpScreen.web'),
);

export type RootStackParamList = {
  AgencySelectionScreen: undefined;
  ShopHomePage: undefined;
  MainLayoutScreen: undefined;
  RegisterUser: undefined;
  SignatureScreen: undefined;
  vinScanner: undefined;
  workOrdersScreen: undefined;
  partsScreen: undefined;
  RepairDetailsScreen: undefined;
  HelpScreen: undefined;
};

const Stack = createStackNavigator<RootStackParamList>();

/** Full-screen centered spinner shown while lazy chunks load */
function ScreenFallback() {
  return (
    <View
      style={{
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#ffffff',
      }}>
      <ActivityIndicator size="large" color="#0071C5" />
    </View>
  );
}

/**
 * AppNavigator
 * Defines the app's main navigation stack for the web.
 * Uses React Navigation's Stack Navigator.
 */

const linking = {
  prefixes: ['http://localhost:3000', 'https://localhost:3000'],
  config: {
    screens: {
      ShopHomePage: 'ShopHomePage',
      AgencySelectionScreen: {
        path: '',
        exact: true,
      },
      // MainLayoutScreen is now URL-tracked so a refresh restores the user
      // to the same screen instead of redirecting to ShopHomePage.
      MainLayoutScreen: 'main',
      RepairDetailsScreen: 'repair-details',
      HelpScreen: 'help',
    },
  },
};

export default function AppNavigator() {
  const dispatch = useDispatch();
  const [isReady, setIsReady] = useState(false);

  // Sync scrollbar thumb colour with the active brand theme (web only).
  // Writes `--scrollbar-thumb` on :root; index.web.css picks it up globally.
  useScrollbarTheme();

  // Bootstrap: hydrate all Redux slices from secureStorage before rendering,
  // mirroring the native AppNavigator pattern so deep-link refreshes work.
  useEffect(() => {
    const bootstrap = async () => {
      try {
      // ── Rehydrate agencyStorage first ────────────────────────────────────
      // Must happen before the agency-from-URL validate call so that
      // X-Agency-Id is present on the request for returning users.
      // Rehydrate agencyStorage from storageService on page reload so that
      // X-Agency-Id is present on all API calls immediately (before any
      // agency-from-URL validation runs).
      if (!agencyStorage.get()) {
        const persistedAgencyId = await storageService.getAgencyCode();
        if (persistedAgencyId) {
          agencyStorage.set(persistedAgencyId);
          console.log(
            '[AppNavigator.web] Rehydrated agencyStorage (X-Agency-Id) from secureStorage:',
            persistedAgencyId,
          );
        }
      }

      // ── Agency-from-subdomain detection ──────────────────────────────────
      // Read the subdomain from window.location.hostname and treat it as the
      // agency name.
      //
      // Production examples:
      //   transit-dev.fastertech.ai        →  agencyName = "transit-dev"
      //   supporttest.fastertechpreprod.ai  →  agencyName = "supporttest"
      //
      // Local-dev fallback:
      //   localhost has no subdomain, so the env var DEV_AGENCY_NAME is used
      //   instead when running on localhost / 127.0.0.1 / 0.0.0.0.
      //   Set DEV_AGENCY_NAME=transit-dev in .env.web to mirror production.
      //
      // To revert to the old URL-path-segment approach, replace this block
      // with the "Agency-from-URL detection" block preserved in git history
      // (search for "Agency-from-URL detection" in the git log).
      //
      // NOTE: validateAgencyName is called on EVERY bootstrap (page load / refresh)
      // regardless of whether agencyStorage is already populated.  This ensures
      // `isNovaAIEnabled` (and any other BFF feature flags returned here) are
      // always fresh — if the BFF flips the flag between sessions the user sees
      // the correct value immediately on next load rather than reading a stale
      // cached value.  agencyStorage.set() is guarded separately so the
      // X-Agency-Id header population still short-circuits on returning visits.
      {
        const hostname = window.location.hostname; // e.g. "transit-dev.fastertech.ai"
        const hostParts = hostname.split('.');
        // A valid subdomain exists when there are ≥ 2 parts and the host is
        // not a numeric IP address.
        const isNumericIp = /^\d+(\.\d+){3}$/.test(hostname);
        const isLocalhost =
          hostname === 'localhost' ||
          hostname === '127.0.0.1' ||
          hostname === '0.0.0.0';

        // 1. Try subdomain from real hostname (production path).
        // 2. Fall back to DEV_AGENCY_NAME env var when on localhost (dev path).
        let subdomainCandidate: string | null = null;
        if (!isNumericIp && !isLocalhost && hostParts.length >= 2) {
          subdomainCandidate = hostParts[0] ?? null;
        } else if (isLocalhost) {
          subdomainCandidate = process.env.DEV_AGENCY_NAME
            ? process.env.DEV_AGENCY_NAME
            : null;
        }

        if (subdomainCandidate) {
          console.log(
            isLocalhost
              ? `[AppNavigator.web] localhost — using DEV_AGENCY_NAME as agency: ${subdomainCandidate}`
              : `[AppNavigator.web] Detected agency name from subdomain: ${subdomainCandidate}`,
          );
          try {
            const validationResult = await validateAgencyName(subdomainCandidate);
            if (validationResult.isValid) {
              // Only populate agencyStorage / storageService when not already set
              // (avoids redundant writes on every page refresh while still
              // ensuring X-Agency-Id is present from the very first request).
              if (!agencyStorage.get()) {
                agencyStorage.set(subdomainCandidate);
                await storageService.setAgencyCode(subdomainCandidate);
                console.log(
                  '[AppNavigator.web] Agency validated — stored as X-Agency-Id:',
                  subdomainCandidate,
                );
              }
              // Always dispatch the latest isNovaAIEnabled from the BFF so the
              // flag reflects any server-side change since the last page load.
              dispatch(setNovaAIEnabled(validationResult.isNovaAIEnabled ?? false));
              console.log(
                '[AppNavigator.web] isNovaAIEnabled:',
                validationResult.isNovaAIEnabled ?? false,
              );
            } else {
              console.warn(
                '[AppNavigator.web] Agency name is invalid:',
                subdomainCandidate,
              );
            }
          } catch (e) {
            console.warn(
              '[AppNavigator.web] Failed to validate agency name:',
              e,
            );
          }
        }
      }
      // ─────────────────────────────────────────────────────────────────────

      // Load language — prefer secureStorage, fall back to storageService for back-compat
      const savedLanguage =
        (await secureStorage.getItem(LANGUAGE_STORAGE_KEY)) ??
        (await storageService.getLanguage());
      if (savedLanguage) {
        dispatch(setLanguage(savedLanguage));
      }

      let token = await storageService.getAuthToken();

      // Probe the live auth session NOW — before setIsReady(true) renders the
      // NavigationContainer — so the token is already fresh when the first API
      // call fires. We always probe (not only when token is missing) because:
      //
      //   1. No stored token: prevents AgencySelectionScreen flash by pre-populating
      //      the token before NavigationContainer mounts.
      //   2. Stored token exists but may be stale (expired while tab was closed):
      //      silently refreshes it via /cookie/me so the very first API call after
      //      a page refresh does not get a 401. The axios interceptor handles 401
      //      via silent refresh too, but calling getUser() here avoids the first
      //      request failing and retrying — cleaner UX with no error flash.
      //
      // The JUST_LOGGED_OUT guard prevents the probe after an intentional logout
      // so we don't re-authenticate from a still-alive server-side cookie.
      let justLoggedOutBootstrap = false;
      try {
        justLoggedOutBootstrap = sessionStorage.getItem('JUST_LOGGED_OUT') === '1';
      } catch {
        /* sessionStorage unavailable */
      }

      if (!justLoggedOutBootstrap) {
        try {
          const oidcUser = await (
            userManager as unknown as {
              getUser(): Promise<{
                access_token?: string;
                expires_at?: number;
              } | null>;
            }
          ).getUser();
          const now = Math.floor(Date.now() / 1000);
          if (oidcUser?.access_token && oidcUser.expires_at && oidcUser.expires_at > now) {
            const isTTAuthSentinel = oidcUser.access_token === 'ttauth-cookie-session';
            if (isTTAuthSentinel) {
              await storageService.setAuthToken('ttauth-cookie-session');
              console.log('[AppNavigator.web] bootstrap: TTAuth cookie session alive (cookie-only) — sentinel stored');
            } else {
              await storageService.setAuthToken(oidcUser.access_token);
              console.log('[AppNavigator.web] bootstrap: live auth session — fresh token stored');
            }
            token = oidcUser.access_token;

            // Rewrite the URL from '/' to '/ShopHomePage' BEFORE setIsReady(true)
            // renders the NavigationContainer.  The linking config maps '/' →
            // AgencySelectionScreen — so if we leave the URL as-is the navigator
            // always mounts on AgencySelectionScreen and then navigates away, causing
            // a visible flash.  Rewriting the URL here means the linking config sees
            // '/ShopHomePage' immediately and the navigator starts directly on
            // ShopHomePage with no navigation at all.
            // Only rewrite when the current path is the root '/' (i.e. the user just
            // returned from the TTAuth redirect) — never override an explicit deep link.
            const currentPath = window.location.pathname.replace(/\/$/, '');
            if (currentPath === '') {
              window.history.replaceState(null, '', '/ShopHomePage');
              console.log('[AppNavigator.web] bootstrap: URL rewritten to /ShopHomePage to prevent AgencySelection flash');
            }
          } else if (token) {
            // getUser() probe returned null/expired — stored token is stale.
            // Clear it so the user lands on AgencySelectionScreen rather than
            // entering an authenticated screen that will immediately get 401s
            // with no recovery path (the interceptor refresh also failed).
            console.warn('[AppNavigator.web] bootstrap: stored token is stale and session probe returned nothing — clearing token');
            await storageService.removeAuthToken();
            token = null;
          }
        } catch (e) {
          console.warn('[AppNavigator.web] bootstrap: getUser() probe failed (non-blocking):', e);
          // Non-blocking: leave whatever token is in storage as-is.
          // The axios interceptor will attempt silent refresh on the first 401.
        }
      }

      if (token) {
        // Hydrate all persisted Redux slices from secureStorage
        const { userIdStorage } = require('../../features/auth/api/userIdStorage');
        const [
          shopRaw,
          technicianRaw,
          menuTab,
          repairId,
          createdUserIdRaw,
          shiftStartedRaw,
          defaultStoreroom,
          roleRaw,
          authUserIdRaw,
          showRepairHistoryRaw,
          maintShopRaw,
        ] = await Promise.all([
          secureStorage.getItem(SHOP_STORAGE_KEY),
          secureStorage.getItem(TECHNICIAN_STORAGE_KEY),
          secureStorage.getItem(MENU_ACTIVE_TAB_KEY),
          secureStorage.getItem(REPAIR_ID_KEY),
          secureStorage.getItem(CREATED_USER_ID_KEY),
          secureStorage.getItem(SHIFT_STARTED_KEY),
          secureStorage.getItem(DEFAULT_STORE_ROOM_KEY),
          secureStorage.getItem('USER_ROLE_KEY'),
          secureStorage.getItem(AUTH_USER_ID_KEY),
          secureStorage.getItem(SHOW_REPAIR_HISTORY_KEY),
          secureStorage.getItem('MAINT_SHOP_KEY'),
        ]);

        // Rehydrate userIdStorage from AUTH_USER_ID_KEY (the logged-in user's
        // own BFF userId from validateAuthRole).  This is intentionally separate
        // from CREATED_USER_ID_KEY which stores the admin-selected technician's
        // ID — using CREATED_USER_ID_KEY here was causing X-UserId to flip to
        // the technician's ID on every page refresh.
        if (authUserIdRaw && authUserIdRaw !== '0') {
          userIdStorage.set(Number(authUserIdRaw));
          console.log('[AppNavigator.web] Rehydrated userIdStorage (auth user) from secureStorage for X-UserId header:', authUserIdRaw);
        }

        if (roleRaw) {
          userIdStorage.setRole(roleRaw);
          console.log('[AppNavigator.web] Rehydrated userIdStorage role from secureStorage:', roleRaw);
        }

        if (maintShopRaw) {
          userIdStorage.setMaintShop(maintShopRaw);
          console.log('[AppNavigator.web] Rehydrated userIdStorage maintShop from secureStorage');
        }

        if (shopRaw) {
          try {
            dispatch(setSelectedShop(JSON.parse(shopRaw) as SelectedShopState));
          } catch {
            /* ignore */
          }
        }
        if (technicianRaw) {
          try {
            dispatch(
              setSelectedTechnicianData(
                JSON.parse(technicianRaw) as SelectedTechnician,
              ),
            );
          } catch {
            /* ignore */
          }
        }
        if (menuTab) {
          dispatch(setActiveTab(menuTab));
        }
        if (repairId) {
          dispatch(setSelectedRepairId(repairId));
        }
        if (createdUserIdRaw && createdUserIdRaw !== '0') {
          dispatch(setCreatedUserId(createdUserIdRaw));
        }
        if (shiftStartedRaw === 'true') {
          dispatch(setShiftStarted(true));
        }
        if (defaultStoreroom) {
          dispatch(setDefaultStoreRoom(defaultStoreroom));
        }
        if (showRepairHistoryRaw === 'true') {
          dispatch(setShowRepairHistory(true));
        }

        // Rehydrate timeZoneStorage from secureStorage so the X-TimeZone header
        // is present on all API calls immediately after a page reload (e.g. IsPinRequired
        // fires before getUserDetails() has a chance to run again).
        const persistedTimezone = await secureStorage.getItem(TIMEZONE_STORAGE_KEY);
        if (persistedTimezone) {
          timeZoneStorage.set(persistedTimezone);
          console.log('[AppNavigator.web] Rehydrated timeZoneStorage:', persistedTimezone);
        }

        // getUserDetails() is intentionally NOT called here.
        // At this point userIdStorage may not have a technician ID yet (fresh
        // Entra login), so the BFF would return the admin/login user's data
        // and populate timeZoneStorage with the wrong timezone.
        // getUserDetails() is called in ShopHomePage immediately after
        // validateAuthRole() sets userIdStorage, guaranteeing the BFF returns
        // the correct user's keyName / timezone.
      }

      // One-time migration: copy any plaintext localStorage / sessionStorage values into
      // secureStorage (so a reload right after migration doesn't lose data), then wipe them.
      try {
        const MIGRATE_KEYS = [
          'AUTH_TOKEN',
          'REFRESH_TOKEN',
          'LANGUAGE',
          'LOGIN_HINT',
          'FORCE_REAUTH',
          SHOP_STORAGE_KEY,
          TECHNICIAN_STORAGE_KEY,
          CREATED_USER_ID_KEY,
          MENU_ACTIVE_TAB_KEY,
          REPAIR_ID_KEY,
          'OPEN_VOICE_MODAL_ON_RETURN',
          LANGUAGE_STORAGE_KEY,
          SHIFT_STARTED_KEY,
          // Legacy plaintext sessionStorage keys written by older app versions
          'selectedShopId',
          'selectedShopName',
          'selectedTechnicianId',
          'selectedTechnicianName',
          'activeTab',
          'shiftStarted',
        ];
        await Promise.all(
          MIGRATE_KEYS.map(async key => {
            const localValue = localStorage.getItem(key);
            if (localValue) {
              // Only migrate if secureStorage doesn't already have the value
              const secureValue = await secureStorage.getItem(key);
              if (!secureValue) {
                await secureStorage.setItem(key, localValue);
              }
              localStorage.removeItem(key);
            }
            // Also clear any residual plaintext sessionStorage entries
            if (typeof sessionStorage !== 'undefined') {
              sessionStorage.removeItem(key);
            }
          }),
        );
      } catch {
        /* ignore — storage may be unavailable in some environments */
      }

      // — Pending success toast (survives page reload) ─────────────────────────
      // DashboardShiftSection.web writes a message to sessionStorage('PENDING_SUCCESS_TOAST')
      // before signoutWithBrowser() triggers window.location.href = origin.
      // We pick it up here so it renders on AgencySelectionScreen via the global
      // ErrorToast in App.tsx.
      try {
        const pendingToastMsg =
          typeof sessionStorage !== 'undefined'
            ? sessionStorage.getItem('PENDING_SUCCESS_TOAST')
            : null;
        if (pendingToastMsg) {
          sessionStorage.removeItem('PENDING_SUCCESS_TOAST');
          dispatch(showErrorToast({toastMessage: pendingToastMsg, successToast: true}));
        }
      } catch {
        /* ignore — sessionStorage may be unavailable */
      }
      // ─────────────────────────────────────────────────────────────────────────

      } catch (err) {
        // Any unhandled error in bootstrap (e.g. IndexedDB blocked in Safari
        // Private Mode, validateAgencyName() network error, etc.) must NOT leave
        // the app permanently blank.  Log the error and fall through to setIsReady
        // so the user at least lands on AgencySelectionScreen.
        console.error('[AppNavigator.web] bootstrap() error — rendering app without hydration:', err);
      } finally {
        setIsReady(true);
      }
    };
    bootstrap();
  }, [dispatch]);

  /**
   * Called once the NavigationContainer is mounted and ready.
   * If a stored access token exists (session still valid) we skip AgencySelectionScreen
   * and navigate straight to ShopHomePage — no OIDC redirect required.
   */
  const handleNavigationReady = useCallback(async () => {
    let token = await storageService.getAuthToken();

    // If signoutWithBrowser() just ran (same page-load cycle, survives the reload
    // via sessionStorage), skip the userManager.getUser() cookie probe entirely.
    // This prevents a race where the server-side TTAuth cookie is still alive when
    // /ttauth/v1/cookie/me fires, causing getUser() to re-persist the sentinel and
    // auto-navigate back to ShopHomePage immediately after logout.
    let justLoggedOut = false;
    try {
      justLoggedOut = sessionStorage.getItem('JUST_LOGGED_OUT') === '1';
      if (justLoggedOut) {
        sessionStorage.removeItem('JUST_LOGGED_OUT');
        console.log('[AppNavigator.web] JUST_LOGGED_OUT flag detected — skipping auth probe');
      }
    } catch {
      /* sessionStorage unavailable — treat as not logged out */
    }

    // Fallback: if secureStorage has no token, probe the live auth session.
    //
    // Entra (oidc-client-ts): getUser() reads the session from localStorage and
    // returns the real JWT; we persist it to secureStorage for future reads.
    //
    // TTAuth/GCIP: getUser() hits GET /ttauth/v1/cookie/me (credentials:include).
    //   • If the backend returns a real JWT in the body, ttAuthAdapter persists it
    //     to secureStorage and returns it here — we also persist it for consistency.
    //   • If the backend only sets a cookie (no body token), ttAuthAdapter returns
    //     the sentinel 'ttauth-cookie-session'. We use it ONLY to trigger
    //     ShopHomePage navigation — we must NOT persist it as a Bearer token.
    //     BFF requests are authenticated via the HttpOnly cookie (withCredentials:true).
    if (!token && !justLoggedOut) {
      try {
        // userManager is typed from the native stub which lacks getUser();
        // on web the real oidc-client-ts / ttAuthAdapter UserManager is used at runtime.
        const oidcUser = await (
          userManager as unknown as {
            getUser(): Promise<{
              access_token?: string;
              expires_at?: number;
            } | null>;
          }
        ).getUser();
        const now = Math.floor(Date.now() / 1000);
        if (oidcUser?.access_token && oidcUser.expires_at && oidcUser.expires_at > now) {
          const isTTAuthSentinel = oidcUser.access_token === 'ttauth-cookie-session';
          if (isTTAuthSentinel) {
            // Cookie-only TTAuth session.
            // Persist the sentinel so the axios response interceptor can detect it
            // via authStorage.getAccessToken() and skip the Bearer-refresh/logout
            // path on 401s.  The request interceptor (axiosInstance.ts line ~100)
            // already guards against sending the sentinel as Authorization: Bearer,
            // so persisting it here is safe.
            await storageService.setAuthToken('ttauth-cookie-session');
            console.log(
              '[AppNavigator.web] TTAuth cookie session detected (cookie-only) — sentinel persisted, navigating to ShopHomePage',
            );
            token = oidcUser.access_token;
          } else {
            // Real JWT (Entra or TTAuth with body token) — persist so subsequent reads succeed.
            console.log(
              '[AppNavigator.web] secureStorage token missing — recovered real JWT from live auth session',
            );
            await storageService.setAuthToken(oidcUser.access_token);
            token = oidcUser.access_token;
          }
        }
      } catch (e) {
        console.warn(
          '[AppNavigator.web] Failed to recover token from auth session:',
          e,
        );
      }
    }

    if (!token) {
      return;
    }

    // Use window.location.pathname to determine the current URL reliably.
    // At the time onReady fires, the linking config may not have finished
    // dispatching the URL-based navigation yet, so getCurrentRoute() can
    // still return the initial route (AgencySelectionScreen) even when the
    // URL is e.g. /main. Checking the pathname directly avoids this race.
    const pathname = window.location.pathname.replace(/\/$/, ''); // strip trailing slash

    // Paths that correspond to a real authenticated screen — do NOT redirect.
    const authenticatedPaths = [
      '/ShopHomePage',
      '/main',
      '/repair-details',
      '/help',
    ];

    if (
      authenticatedPaths.some(
        p => pathname === p || pathname.startsWith(p + '/'),
      )
    ) {
      console.log(
        `[AppNavigator.web] Stored token found — staying on URL path: ${pathname}`,
      );
    } else {
      // Root path '/', empty, or any unrecognised path → go to ShopHomePage.
      // React Navigation's onReady fires from a passive effect commit; the
      // internal state machine may not have finished mounting the initial route
      // yet. Poll isReady() via rAF until the navigator confirms it is ready,
      // then navigate — avoids the "navigation object hasn't been initialized"
      // error that a single rAF deferral cannot reliably prevent.
      console.log(
        '[AppNavigator.web] Stored token found — navigating to ShopHomePage',
      );
      const navigateWhenReady = () => {
        if (navigationRef.current?.isReady()) {
          navigationRef.current.navigate('ShopHomePage' as never);
        } else {
          requestAnimationFrame(navigateWhenReady);
        }
      };
      requestAnimationFrame(navigateWhenReady);
    }
  }, []);

  // ── Proactive TTAuth cookie refresh ──────────────────────────────────────
  // The TTAuth session cookie (__ttauthpreprod) has a ~1-hour server-side TTL.
  // Without proactive renewal, the cookie expires silently while the user is
  // idle on a screen (e.g. RepairDetails for 1 hour).  The next API call then
  // fires without a valid cookie, receives a server error, and the axios 401
  // interceptor triggers the reactive /cookie/me fetch — but the first request
  // has already failed and the error is visible to the user.
  //
  // This effect closes the gap by:
  //   1. Scheduling a 50-minute interval to renew the cookie before it expires.
  //   2. Listening for visibilitychange so a refresh fires the moment the user
  //      returns to the tab after it was backgrounded for an extended period.
  //
  // Both paths call refreshAccessToken() (ttAuthAdapter.web → /cookie/me) only
  // when the user is authenticated (token present) and has not just logged out.
  // The axiosInstance isRefreshing mutex handles any accidental concurrency with
  // the reactive interceptor path.
  useEffect(() => {
    // Time in ms between proactive refreshes.  50 min < 60-min cookie TTL.
    const PROACTIVE_REFRESH_INTERVAL_MS = 50 * 60 * 1000;
    // Minimum idle time (ms) before a visibilitychange re-triggers a refresh.
    // Avoids hammering /cookie/me when the user rapidly alt-tabs.
    const VISIBILITY_REFRESH_THRESHOLD_MS = 5 * 60 * 1000;

    let lastRefreshAt = Date.now();

    const doProactiveRefresh = async (reason: string) => {
      // Skip if JUST_LOGGED_OUT (user intentionally signed out in this tab).
      try {
        if (sessionStorage.getItem('JUST_LOGGED_OUT') === '1') {
          return;
        }
      } catch { /* ignore */ }

      // Only refresh when the user is authenticated.
      const token = await storageService.getAuthToken();
      if (!token) {
        return;
      }

      try {
        console.log(`[AppNavigator.web] Proactive token refresh — reason: ${reason}`);
        const newToken = await refreshAccessToken();
        if (newToken) {
          lastRefreshAt = Date.now();
          console.log('[AppNavigator.web] Proactive token refresh succeeded');
        } else {
          // Refresh returned null — session fully expired; let the next API call
          // trigger the 401 interceptor path which calls onSessionExpired().
          console.warn('[AppNavigator.web] Proactive token refresh returned null — session may be expired');
        }
      } catch (err) {
        // Non-fatal: the 401 interceptor is the safety net.
        console.warn('[AppNavigator.web] Proactive token refresh threw:', err);
      }
    };

    // 1. Periodic interval — fires every 50 minutes regardless of user activity.
    const intervalId = setInterval(() => {
      doProactiveRefresh('50-min interval');
    }, PROACTIVE_REFRESH_INTERVAL_MS);

    // 2. visibilitychange — fires when the user switches back to this tab.
    //    Only triggers a refresh if >= VISIBILITY_REFRESH_THRESHOLD_MS have
    //    elapsed since the last refresh (avoids redundant calls).
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const msSinceLastRefresh = Date.now() - lastRefreshAt;
        if (msSinceLastRefresh >= VISIBILITY_REFRESH_THRESHOLD_MS) {
          doProactiveRefresh(`visibilitychange (idle ${Math.round(msSinceLastRefresh / 60000)} min)`);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);
  // ─────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    // Register the global session-expired handler.
    // Called by the axios interceptor when both access and refresh tokens are fully expired.
    // Clears auth state (setForceReauth is included in signoutWithBrowser) and returns
    // the user to AgencySelectionScreen where they must sign in again.
    setSessionExpiredCallback(async () => {
      console.warn(
        '[AppNavigator] Session fully expired — redirecting to AgencySelectionScreen',
      );
      tabManager.broadcastSessionExpired();
      await signoutWithBrowser();
      // signoutWithBrowser on web does window.location.href = origin, which reloads the app
      // and lands on AgencySelectionScreen. No further navigation needed here.
    });

    // Listen for cross-tab auth events (SESSION_EXPIRED / SIGNED_OUT broadcast from
    // another tab) and sign this tab out immediately so all tabs stay in sync.
    const unsubAuthEvent = tabManager.onAuthEvent(async event => {
      if (event === 'SESSION_EXPIRED' || event === 'SIGNED_OUT') {
        await signoutWithBrowser();
      }
    });

    return () => {
      unsubAuthEvent();
    };
  }, []);

  // Don't render until secureStorage bootstrap is done — prevents stale state flash
  if (!isReady) {
    return null;
  }

  return (
    <NavigationContainer
      ref={navigationRef}
      linking={linking}
      onReady={handleNavigationReady}
      onStateChange={() => {
        // Track SPA route changes in Pendo.
        // window.location.href reflects the updated URL at this point because
        // React Navigation has already called window.history.pushState/replaceState.
        pendoService.pageLoad(window.location.href);
      }}
      documentTitle={{
        formatter: (options, route) => {
          const screenTitles: Record<string, string> = {
            AgencySelectionScreen: 'Agency Selection',
            ShopHomePage: 'Shop Home',
            MainLayoutScreen: 'Work Orders',
            RepairDetailsScreen: 'Repair Details',
            HelpScreen: 'Help',
          };
          const screenName = route?.name ?? '';
          const screenTitle = screenTitles[screenName] ?? screenName;
          return `${screenTitle} — Faster Technician`;
        },
      }}>
      {/* §2.1 — Suspense boundary wraps the entire navigator so lazy chunks can load */}
      <Suspense fallback={<ScreenFallback />}>
        <Stack.Navigator
          initialRouteName="AgencySelectionScreen"
          screenOptions={{
            headerShown: false,
          }}>
          {/* Authentication and onboarding screens */}
          <Stack.Screen
            name="AgencySelectionScreen"
            component={AgencySelectionScreen}
            options={{
              header: () => <Header showLanguageCTA />,
              headerShown: true,
            }}
          />

          {/* Main application screens */}
          <Stack.Screen
            name="ShopHomePage"
            component={ShopHomePage}
            options={{
              header: () => (
                <Header
                  showHelpCTA
                  showLanguageCTA
                  showLogoutCTA
                  showSelectedShop
                />
              ),
              headerShown: true,
            }}
          />
          <Stack.Screen
            name="MainLayoutScreen"
            component={MainLayoutScreen}
            options={{
              headerShown: true,
              gestureEnabled: false,
            }}
          />
          <Stack.Screen name="SignatureScreen" component={SignatureScreen} />
          <Stack.Screen
            name="workOrdersScreen"
            component={WorkOrdersScreen}
            options={{headerShown: false}}
          />
          <Stack.Screen
            name="partsScreen"
            component={PartsScreen}
            options={{headerShown: false}}
          />
          <Stack.Screen
            name="RepairDetailsScreen"
            component={RepairDetailsScreen}
            options={{headerShown: false}}
          />
          <Stack.Screen
            name="HelpScreen"
            component={HelpScreen}
            options={{
              headerShown: true,
              gestureEnabled: false,
            }}
          />
        </Stack.Navigator>
      </Suspense>
    </NavigationContainer>
  );
}
